/**
 * @file XeroRequiredGate.js
 * @version 2.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-27
 * @modified 2026-10-03
 *
 * The blocking prompt shown when the app cannot carry on until somebody acts.
 *
 * Two reasons, and they are not the same problem:
 *
 *   'session' — the sign-in has expired. This applies to EVERY account, not
 *     only the ones that use Xero. Until this existed a lapsed cookie was
 *     invisible: the page went on looking signed in while every request failed,
 *     and the user found out by watching a save do nothing. There is exactly
 *     one way out of it, so exactly one thing is offered — signing in again.
 *     Reconnecting Xero is not offered, because it cannot work: that flow is
 *     itself authenticated, so it would fail the same way and read as the
 *     system being broken rather than as needing a sign-in.
 *
 *   'xero' — the session is fine and the Xero connection is not. Most of this
 *     system reads and writes through Xero as the signed-in user — every call
 *     is preceded by xeroService.initializeForUser(req.userId) — so for those
 *     accounts a missing connection is not a degraded experience, it is a
 *     system that fails on the first thing you try. Here reconnecting IS the
 *     fix, so it leads.
 *
 * The way out is a link rather than a button in both cases. A slide-out button
 * is for an action within the app; this ends the session and starts again, and
 * dressing it up as an ordinary control overstates it.
 *
 * Not shown to every account for the Xero reason: roles carry `requires_xero`,
 * and the REP badge is 'blocked' — a rep reads what has already been synced,
 * never talks to Xero, and must never be asked to connect. The decision is made
 * server-side and arrives as `user.xero`. The session reason has no such
 * exemption; everybody has to be signed in.
 *
 * When the Xero reason applies, and when it must not (Dashboard decides):
 *   - only on a real answer from /api/auth/xero/status saying "not connected".
 *     A network blip, a 502 mid-deploy or the status route's own 503 keeps the
 *     last known state; before the first answer the check is still "checking",
 *     and the gate never shows while checking. It used to appear on any failed
 *     poll and stay up for five minutes.
 *   - never in custom-connection mode (XERO_AUTH_MODE=custom): the system has
 *     one Xero connection of its own and nobody has anything to connect. A
 *     problem with that connection is the advisory XeroHealthBanner's job.
 *
 * Contrast with MaintenanceBanner, which is deliberately advisory: a deploy
 * lasts ten seconds and blocking reading costs more than it saves. This is the
 * opposite case — the block persists until someone fixes it, and until they do,
 * nothing works.
 */

import React from 'react';
import { useSlideOutButton } from './SlideOutButton';
import './XeroRequiredGate.css';

function XeroRequiredGate({ onConnect, onLogout, checking, reason = 'xero' }) {
  const sessionExpired = reason === 'session';

  // Measures the .btn-slideout label so the button expands to the right width
  // instead of staying at its collapsed 45px. Only the Xero reason renders one.
  useSlideOutButton([checking, reason]);

  return (
    <div className="xero-gate-overlay" role="alertdialog" aria-modal="true" aria-labelledby="xero-gate-title">
      <div className="xero-gate-panel">
        <span className="material-symbols-rounded xero-gate-icon">
          {sessionExpired ? 'lock_clock' : 'link_off'}
        </span>

        {sessionExpired ? (
          <>
            <h2 id="xero-gate-title">Your session has expired</h2>
            <p>
              You have been signed out for security. Anything you were part-way
              through has not been saved.
            </p>
            <p className="xero-gate-note">
              Signing in again takes you back to where you were.
            </p>
          </>
        ) : (
          <>
            <h2 id="xero-gate-title">Xero is not connected</h2>
            <p>
              Your account works through Xero, and the connection has either expired or
              has not been made yet. Almost nothing in the system will work until it is
              reconnected — invoices, purchase orders, contacts and syncing all run
              against Xero as you.
            </p>
            <p className="xero-gate-note">
              Connecting takes a few seconds and sends you to Xero to sign in. You will
              come straight back here.
            </p>
          </>
        )}

        {/* Reconnecting is offered ONLY when the session is still good. With an
            expired session the Xero flow is authenticated too, so it would fail
            the same way and look like a broken system rather than a sign-in. */}
        {!sessionExpired && (
          <div className="buttons slideout xero-gate-actions">
            <div>
              <button className="btn-slideout" onClick={onConnect} disabled={checking}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">link</span>
                </div>
                <span className="btn-label">{checking ? 'Checking…' : 'Connect to Xero'}</span>
              </button>
            </div>
          </div>
        )}

        <button type="button" className="xero-gate-link" onClick={onLogout}>
          {sessionExpired ? 'Sign in again' : 'Sign out and sign in again'}
        </button>
      </div>
    </div>
  );
}

export default XeroRequiredGate;
